/**
 * `GET /exploration/map` — the hex territory overlay (`apps/api/src/exploration/map-overlay.ts`).
 * This hook issues its own `/exploration/map` request under its own query
 * key (`['exploration-map', propertyId]`); it calls `useExplorationCycles`
 * only to read the active-cycle gate for its `refetchInterval` (5s while a
 * cycle is active, per Decision 3, else off) — that *inner* call shares its
 * query key with the page's own cycles query, so it's the one deduped
 * against the page, not this hook's own overlay request.
 *
 * @module features/exploration/map/useExplorationMap
 */

import { useQuery } from '@tanstack/react-query';
import type { ExplorationMapOverlay } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { isActiveCycleStatus, useExplorationCycles } from '../useExploration';

export function useExplorationMap(propertyId: string | undefined) {
  // This inner call shares its query key with `useExplorationCycles(propertyId)`
  // elsewhere on the page, so it's deduped against the page's own cycles
  // fetch rather than firing a second request. Polling follows the CYCLES
  // query's active status, not the overlay's own `cycle` field — right after
  // "Run now" the overlay can still read `cycle: null` for one tick (the new
  // cycle hasn't reached `exploring` yet), and gating on that would stop the
  // poll before worker dots ever appear.
  const cyclesQ = useExplorationCycles(propertyId);
  const cycles = cyclesQ.data?.cycles ?? [];
  const hasActiveCycle = cycles.some((c) => isActiveCycleStatus(c.status));

  return useQuery({
    queryKey: ['exploration-map', propertyId ?? 'all'],
    enabled: Boolean(propertyId),
    queryFn: async (): Promise<ExplorationMapOverlay> => {
      const url = `${API_URL}/exploration/map?propertyId=${encodeURIComponent(propertyId!)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch the exploration map');
      const json = (await res.json()) as { data: ExplorationMapOverlay };
      return json.data;
    },
    refetchInterval: hasActiveCycle ? 5000 : false,
  });
}
